import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  useTranslate,
  useCosts,
  $$,
  makePlanUrlPartForApi,
} from "../../../helpers";
import { PharmacyType, Plan, PlanDetailsSection } from "../../../@types";
import { Cell, CheckmarkIcon, PlanDetailsHashLink } from "../../../components";
import { AppContext } from "../../../app/store";
import {
  shouldSuppressDrugPricing,
  shouldSuppressPlanBenefits,
} from "../../../helpers/planHelpers";
import { getOtherDrugInfo } from "../../../api";

const DrugCostsAndCoverage: FunctionComponent<{
  plan: Plan;
}> = ({ plan }) => {
  const t = useTranslate();
  const { state } = useContext(AppContext);
  const { costs } = useCosts(plan);
  const [numCoveredDrugs, setNumCoveredDrugs] = useState<number | undefined>(0);

  const loadOtherDrugInfo = async (): Promise<void> => {
    if (state.prescriptions.length) {
      const ndcs = state.prescriptions.map(p => p.ndc);
      const planUrl = makePlanUrlPartForApi(plan);
      try {
        const otherDrugInfo = await getOtherDrugInfo(ndcs, planUrl);

        const covered =
          otherDrugInfo?.reduce(
            (prev, curr) => prev + (curr.on_formulary ? 1 : 0),
            0
          ) ?? 0;

        setNumCoveredDrugs(covered);
      } catch (_e) {
        setNumCoveredDrugs(undefined);
      }
    }
  };

  useEffect(() => {
    loadOtherDrugInfo();
  }, []);

  const getPharmacyName = (npi: string): string => {
    if (npi === "") {
      return t("plan_search_prefs.mail_order_pharmacy");
    }

    const pharmacy = state.pharmacies.find(p => p.npi === npi);

    return pharmacy ? pharmacy.name : "N/A";
  };

  const suppressDrugPricing = shouldSuppressDrugPricing(plan);
  const showMailOrderNotCovered =
    !costs.find(p => p.mail_order === true) &&
    [PharmacyType.MAIL_AND_RETAIL, PharmacyType.MAIL].includes(
      state.pharmacyType
    );

  const mailOrderNotCoveredElem = (
    <div className="PharmacyItem" data-testid="mail-order-not-covered">
      <span className="DrugCosts__label">{getPharmacyName("")}</span>
      <div>
        <CheckmarkIcon checked={false} />
        {` `}
        {t("plan_terms.does_not_cover_mail_order")}
      </div>
    </div>
  );

  return (
    <div className="columnSection">
      <Cell>
        <div>
          <div className="DetailItem__label mobileOnly">
            {t("plan_terms.drugs_covered")}
          </div>
          {shouldSuppressPlanBenefits(plan) ? (
            <div>{t("suppressed_plan_benefit")}</div>
          ) : numCoveredDrugs === undefined ? (
            <div className="ds-u-color--error">{t("error.drug_info")}</div>
          ) : (
            <>
              <div className="DrugCosts__label">{`${numCoveredDrugs} ${t(
                "of"
              )} ${state.prescriptions.length}`}</div>
              <div>{t("plan_terms.prescription_drugs_covered")}</div>
              <PlanDetailsHashLink
                label={t("restrictions_may_apply")}
                plan={plan}
                section={PlanDetailsSection.OTHER_DRUG_INFO}
              />
            </>
          )}
        </div>
      </Cell>

      {!costs.length && showMailOrderNotCovered && (
        <Cell>
          <div className="DetailItem__label mobileOnly">
            {t("plan_terms.estimated_yearly_total_cost")}
          </div>
          <div className="DrugCosts__list">{mailOrderNotCoveredElem}</div>
        </Cell>
      )}

      {costs.length > 0 && (
        <Cell>
          <div className="DetailItem__label mobileOnly">
            {t("plan_terms.estimated_yearly_total_cost")}
          </div>
          <div className="DrugCosts__list">
            {costs.map(pharmacy => {
              if (
                pharmacy.mail_order &&
                [PharmacyType.RETAIL, PharmacyType.NONE].includes(
                  state.pharmacyType
                )
              ) {
                return null;
              }

              return (
                <div key={pharmacy.npi} className="PharmacyItem">
                  <span className="DrugCosts__label">
                    {getPharmacyName(pharmacy.npi)}
                  </span>
                  <div>
                    <CheckmarkIcon checked={pharmacy.in_network} />
                    {` `}
                    {pharmacy.preferred
                      ? t("pharmacy.terms.preferred_network")
                      : pharmacy.in_network
                      ? t("pharmacy.terms.standard_in_network")
                      : t("pharmacy.terms.out_of_network")}
                  </div>
                  <span>
                    {suppressDrugPricing ? (
                      <i>{t("plan_terms.drug_pricing_suppressed")}</i>
                    ) : (
                      $$(
                        plan.total_remaining_premium +
                          pharmacy.estimated_yearly_total
                      )
                    )}
                  </span>
                </div>
              );
            })}
            {showMailOrderNotCovered && mailOrderNotCoveredElem}
          </div>
        </Cell>
      )}
    </div>
  );
};

export default DrugCostsAndCoverage;
